import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCart } from '../context/CartContext'
import './Checkout.css'

export default function Checkout() {
    const { cart, cartTotal, clearCart } = useCart()
    const navigate = useNavigate()
    const [step, setStep] = useState(1)
    const [orderComplete, setOrderComplete] = useState(false)
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        phone: ''
    })

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (step === 1) {
            setStep(2)
        } else {
            // Simulate order completion
            setOrderComplete(true)
            clearCart()
        }
    }

    if (cart.length === 0 && !orderComplete) {
        return (
            <div className="checkout-page">
                <div className="container">
                    <div className="empty-checkout">
                        <h2>Your cart is empty</h2>
                        <Link to="/products" className="btn btn-primary">
                            Continue Shopping
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    if (orderComplete) {
        return (
            <div className="checkout-page">
                <div className="container">
                    <div className="order-success">
                        <div className="success-icon">✓</div>
                        <h1>Order Confirmed!</h1>
                        <p>Thank you for your purchase. You'll receive a confirmation email shortly.</p>
                        <p className="order-number">Order #SW-{Date.now().toString().slice(-8)}</p>
                        <Link to="/products" className="btn btn-primary">
                            Continue Shopping
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="checkout-page">
            <div className="container">
                <h1>Checkout</h1>

                {/* Progress */}
                <div className="checkout-progress">
                    <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
                        <span className="step-number">1</span>
                        <span className="step-label">Shipping</span>
                    </div>
                    <div className="progress-line"></div>
                    <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
                        <span className="step-number">2</span>
                        <span className="step-label">Payment</span>
                    </div>
                </div>

                <div className="checkout-layout">
                    <form className="checkout-form" onSubmit={handleSubmit}>
                        {step === 1 && (
                            <div className="form-section card">
                                <h2>Shipping Information</h2>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row two-col">
                                    <div className="form-group">
                                        <label>First Name</label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Last Name</label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Address</label>
                                        <input
                                            type="text"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row three-col">
                                    <div className="form-group">
                                        <label>City</label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>State</label>
                                        <input
                                            type="text"
                                            name="state"
                                            value={formData.state}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>ZIP Code</label>
                                        <input
                                            type="text"
                                            name="zip"
                                            value={formData.zip}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <button type="submit" className="btn btn-primary btn-lg">
                                    Continue to Payment
                                </button>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="form-section card">
                                <h2>Payment</h2>
                                <p className="payment-notice">
                                    This is a demo checkout. In production, integrate with a payment
                                    gateway like Stripe or PayPal.
                                </p>

                                <div className="form-group">
                                    <label>Card Number</label>
                                    <input type="text" placeholder="4242 4242 4242 4242" disabled />
                                </div>

                                <div className="form-row two-col">
                                    <div className="form-group">
                                        <label>Expiry</label>
                                        <input type="text" placeholder="MM/YY" disabled />
                                    </div>
                                    <div className="form-group">
                                        <label>CVC</label>
                                        <input type="text" placeholder="123" disabled />
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>
                                        Back
                                    </button>
                                    <button type="submit" className="btn btn-primary btn-lg">
                                        Place Order
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>

                    {/* Order Summary */}
                    <div className="order-summary card">
                        <h3>Order Summary</h3>
                        <div className="summary-items">
                            {cart.map(item => (
                                <div key={item.id} className="summary-item">
                                    <span className="item-name">
                                        {item.name} × {item.quantity}
                                    </span>
                                    <span className="item-price">
                                        ${(parseFloat(item.price) * item.quantity).toFixed(2)}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div className="summary-totals">
                            <div className="summary-row">
                                <span>Subtotal</span>
                                <span>${cartTotal.toFixed(2)}</span>
                            </div>
                            <div className="summary-row">
                                <span>Shipping</span>
                                <span>Free</span>
                            </div>
                            <div className="summary-row total">
                                <span>Total</span>
                                <span>${cartTotal.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
